import Box from '@mui/material/Box';
import type {
  ColDef,
  GridReadyEvent,
  RowClickedEvent,
  SortChangedEvent,
  StateUpdatedEvent
} from 'ag-grid-enterprise';
import { AgGridReact } from 'ag-grid-react';
import { useAtom } from 'jotai';
import { useCallback, useEffect, useMemo } from 'react';

import LoadingIndicator from '@/components/LoadingIndicator';
import {
  ASSET_GRID_OPTIONS,
  ASSETS_TABLE_DEFAULT_SORT_MODEL,
  getAssetColumnDefs
} from '@/features/AssetTable/AssetTable.constants';
import useGridTheme from '@/hooks/useGridTheme';
import {
  assetRiskScoreDialogAtom,
  assetsGridApiAtom,
  assetsTableOrderAtom,
  assetsTableOrderByAtom,
  filteredAssetsAtom,
  selectedAssetAtom,
  useAssetSelection
} from '@/pages/Assets';
import {
  selectedOrganizationAtom,
  selectedOrganizationIdAtom
} from '@/pages/Organizations';
import {
  gridGetStoredState,
  gridSetPage,
  gridSetStoreState
} from '@/utils/Grid.utils';

const AssetTable = ({ isLoading }: { isLoading: boolean }) => {
  const { selectAsset } = useAssetSelection();
  const [assetsGridApi, setAssetsGridApi] = useAtom(assetsGridApiAtom);
  const [assetsTableOrder, setAssetsTableOrder] = useAtom(assetsTableOrderAtom);
  const [assetsTableOrderBy, setAssetsTableOrderBy] = useAtom(
    assetsTableOrderByAtom
  );
  const [filteredAssets] = useAtom(filteredAssetsAtom);
  const [selectedAsset] = useAtom(selectedAssetAtom);
  const [selectedOrganization] = useAtom(selectedOrganizationAtom);
  const [selectedOrganizationId] = useAtom(selectedOrganizationIdAtom);
  const [, setAssetRiskScoreDialog] = useAtom(assetRiskScoreDialogAtom);

  const gridTheme = useGridTheme();

  const handleRowClick = (event: RowClickedEvent) => {
    const target = event.event?.target as HTMLElement;
    const isRiskScoreCell =
      target?.closest('[col-id="risk_score"]') ||
      target?.closest('.risk-score-badge');
    if (isRiskScoreCell) {
      setAssetRiskScoreDialog(event.data);
      return; // Don't select the row
    }
    // Regular row selection
    selectAsset(event.data.id);
  };

  const handleSortChanged = (params: SortChangedEvent) => {
    const state = params.api.getColumnState();
    const sorted = state.find((col) => col.sort);

    if (sorted) {
      setAssetsTableOrder(sorted.sort ?? null);
      setAssetsTableOrderBy(sorted.colId);
      return;
    }

    setAssetsTableOrder(null);
    setAssetsTableOrderBy(null);
  };

  const columnDefs = useMemo(
    () => getAssetColumnDefs(selectedOrganization.label),
    [selectedOrganization]
  );

  // Only apply column state from local storage on initial grid ready
  useEffect(() => {
    if (
      assetsGridApi &&
      !assetsGridApi.isDestroyed() &&
      assetsTableOrderBy &&
      assetsTableOrder
    ) {
      assetsGridApi.applyColumnState({
        state: [{ colId: assetsTableOrderBy, sort: assetsTableOrder }]
      });
    }
  }, [assetsGridApi]);

  useEffect(() => {
    // on selected asset change - select or deselect asset table row
    if (assetsGridApi && selectedAsset && !assetsGridApi.isDestroyed()) {
      const selectedRows = assetsGridApi.getSelectedRows();
      if (selectedRows?.[0]?.id === parseInt(selectedAsset.id.toString(), 10)) {
        return;
      }
      assetsGridApi.forEachNode((node) => {
        if (node.data.id === parseInt(selectedAsset.id.toString(), 10)) {
          node.setSelected(true, true);
        }
      });
    }
  }, [assetsGridApi, filteredAssets, selectedAsset]);

  const handleGridReady = (params: GridReadyEvent) => {
    // Store the GridApi for later use
    setAssetsGridApi(params.api);
  };

  const handleStateUpdate = useCallback(
    (event: StateUpdatedEvent) => gridSetStoreState(event, 'AssetsTable'),
    []
  );

  useEffect(() => {
    if (assetsGridApi && !assetsGridApi.isDestroyed()) {
      assetsGridApi.paginationGoToFirstPage();
    }
  }, [selectedOrganizationId]);

  const gridOptions = useMemo(() => {
    // restore sorting and pagination state on mount
    const initialState = gridGetStoredState(
      'AssetsTable',
      ASSETS_TABLE_DEFAULT_SORT_MODEL
    );
    return {
      ...ASSET_GRID_OPTIONS,
      initialState,
      onRowClicked: handleRowClick,
      onGridReady: handleGridReady,
      onStateUpdated: handleStateUpdate
    };
  }, []);

  return (
    <Box
      className="ag-theme-alpine rounded-4px"
      sx={{ border: 'none !important' }}
    >
      <LoadingIndicator isLoading={isLoading} />
      <AgGridReact
        columnDefs={columnDefs as ColDef[]}
        getRowId={(params) => params.data.id.toString()}
        gridOptions={gridOptions}
        rowData={filteredAssets}
        theme={gridTheme}
        onGridReady={(params) => {
          setAssetsGridApi(params.api);
        }}
        onRowClicked={(event) => {
          handleRowClick(event);
        }}
        onSortChanged={(params) => {
          handleSortChanged(params as SortChangedEvent);
        }}
        onFirstDataRendered={(params) =>
          gridSetPage(params.api, 'AssetsTableState')
        }
      />
    </Box>
  );
};

export default AssetTable;
